/* eslint-disable react/no-unstable-nested-components */
import useDocumentTitle from '../hooks/useDocumentTitle';
// v3.15-1226 OPS-001 — Priority Queue widget on every employee dashboard.
// v3.15-1235 — PriorityQueueWidget removed. Priorities live inside the Ghost Daily Organizer.
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { userCanUseBooking } from '../lib/bookingScope';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import WorkflowDashboard from '../components/WorkflowDashboard';
import ActionFocusedDashboard from '../components/ActionFocusedDashboard';
import UpcomingAppointmentsWidget from '../components/UpcomingAppointmentsWidget';
import AppointmentAlertBanner from '../components/AppointmentAlertBanner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Users,
  UserPlus,
  Clock,
  CheckCircle,
  ClipboardList,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  FileText,
  DollarSign,
  Target,
  Briefcase,
  Percent,
  Activity,
  Timer,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  BarChart3,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
  Zap,
  User,
  Trophy,
  Gauge,
  Trash2,
  Send,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatET } from '../utils/timeUtils';
// v3.18-1278 GHOST-OPS-001 — Final Push composer uses Dialog + Input + Label + Textarea.
// v3.18-1290 EMAIL-ARCH-002 Slice 2 rollout — Final Push composer is
// now rendered by the universal ComposeAndApprove component; the old
// bespoke FinalPushComposer dialog has been retired.
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ComposeAndApprove from '../components/ComposeAndApprove';

const stageColors = {
  new_lead: 'bg-blue-100 text-blue-800',
  contacted: 'bg-cyan-100 text-cyan-800',
  qualifying: 'bg-indigo-100 text-indigo-800',
  application_submitted: 'bg-purple-100 text-purple-800',
  under_review: 'bg-amber-100 text-amber-800',
  offer_selected: 'bg-green-100 text-green-800',
  funding_pending: 'bg-orange-100 text-orange-800',
  customer_funded: 'bg-emerald-100 text-emerald-800',
  closed_declined: 'bg-slate-100 text-slate-800',
};

const activityTypeIcons = {
  stage_change: TrendingUp,
  status_change: TrendingUp,
  note_added: FileText,
  internal_note_added: FileText,
  document_uploaded: FileText,
  task_created: ClipboardList,
  task_completed: CheckCircle,
  assignment_change: Users,
  client_created: UserPlus,
  deal_updated: DollarSign,
  funding_updated: DollarSign,
};
// v3.15-951-BOOKING-FIX: Admin-only warning when any user has a missing or
// duplicate booking slug. Prevents silent broken /book/:slug links.
// v3.15-952-BULK-SLUG: one-click "Generate Missing Slugs" button.
// v3.15-957-BOOKING-PTC-ONLY-HARD-LOCK: this warning is now hidden for any
// user outside the booking-allowed companies (PTC / Prestigious Travel
// Club Inc) — the entire booking system is gated to those scopes.
// v3.15-1036: Exported for reuse by AdminDiagnosticsPage (operational dashboard
// no longer renders this widget — diagnostics are admin-only and surfaced from
// /admin/diagnostics).
export const BookingSlugIssuesWarning = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [fixing, setFixing] = useState(false);
  const eligible = userCanUseBooking(user);
  const refresh = async () => {
    try {
      const res = await api.get('/appointments/admin/booking-slug-issues');
      setData(res.data);
    } catch { /* non-admin gets 403 — silently hide */ }
  };
  useEffect(() => {
    if (!eligible) { setData(null); return; }
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/appointments/admin/booking-slug-issues');
        if (alive) setData(res.data);
      } catch (e) { console.warn('[DashboardPage] booking-slug-issues fetch failed', e); }
    })();
    return () => { alive = false; };
  }, [eligible]);
  const fixAll = async () => {
    if (fixing) return;
    if (!window.confirm('Generate booking slugs for all users with missing slugs?')) return;
    setFixing(true);
    try {
      const res = await api.post('/appointments/admin/booking-slug-issues/fix-all');
      const fixed = res.data?.count || 0;
      toast.success(fixed ? `Generated ${fixed} booking slug${fixed === 1 ? '' : 's'}` : 'No missing slugs to fix');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate slugs');
    } finally {
      setFixing(false);
    }
  };
  if (!eligible) return null;
  if (!data || !data.count) return null;
  const onlyMissing = (data.issues || []).every(u => u.reason === 'missing');
  return (
    <div className="mt-3" data-testid="booking-slug-issues-warning">
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Booking link inactive or misconfigured
              </p>
              <p className="text-[12px] text-amber-800 mt-0.5">
                {data.count} user{data.count === 1 ? '' : 's'} {data.count === 1 ? 'has' : 'have'} a missing or duplicate booking slug. Their{' '}
                <span className="font-mono">/book/:slug</span> link will fail to load.
              </p>
            </div>
            {onlyMissing && (
              <button
                onClick={fixAll}
                disabled={fixing}
                className="text-[11px] font-semibold px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60 whitespace-nowrap"
                data-testid="generate-missing-slugs-btn"
              >
                {fixing ? 'Generating…' : 'Generate Missing Slugs'}
              </button>
            )}
          </div>
          <ul className="mt-1.5 space-y-0.5 text-[11px] text-amber-800">
            {(data.issues || []).slice(0, 5).map(u => (
              <li key={u.user_id}>
                • {u.full_name || u.email || u.user_id} — <span className="italic">{u.reason}</span>
              </li>
            ))}
            {(data.issues || []).length > 5 && (
              <li>• …and {(data.issues || []).length - 5} more</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};


// v3.15-915: Unified Reconcile All chip — single morning-coffee number that
// combines Funded Without Payment + Funded But Active. Hidden when total = 0.
// v3.15-1036: Exported for reuse by AdminDiagnosticsPage.
export const ReconcileAllChip = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const res = await api.get('/admin/reconcile-summary');
        if (alive) setData(res.data);
      } catch (_e) { /* admin-only — silent for non-admins */ }
    };
    fetch();
    const t = setInterval(fetch, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!data || (data.total_count ?? 0) === 0) return null;
  const fwp = data.funded_without_payment || {};
  const fba = data.funded_but_active || {};
  return (
    <div className="mt-3" data-testid="reconcile-all-chip-row">
      <button
        type="button"
        onClick={() => navigate('/admin/reconcile')}
        className="flex items-center gap-3 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors w-full md:w-auto"
        data-testid="reconcile-all-chip"
      >
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-amber-600 text-white text-sm font-bold">
          {data.total_count}
        </span>
        <span className="text-sm font-medium text-amber-900">Reconcile</span>
        <span className="text-[11px] text-amber-700">
          {fwp.count || 0} no payment · {fba.count || 0} stuck active
        </span>
        <span className="text-[10px] text-amber-700 uppercase tracking-wider ml-auto">open →</span>
      </button>
    </div>
  );
};


// v3.15-1007: SSOT Integrity Chip — surfaces commission orphans + legacy
// settlement candidates without forcing the admin to drill into multiple
// pages. Uses /admin/funded-mismatch-chip (counts only — chip is fast and
// non-sensitive). Clicking the chip opens FundedMismatchesPage; the
// detailed legacy-settlement-candidates list is reachable from there in
// future iterations. READ-ONLY by design — no fix actions on the chip.
// v3.15-1036: Exported for reuse by AdminDiagnosticsPage.
export const SSOTIntegrityChip = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const res = await api.get('/admin/funded-mismatch-chip');
        if (alive) setData(res.data);
      } catch (_e) { /* admin-only — silent for non-admins */ }
    };
    fetch();
    const t = setInterval(fetch, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!data) return null;
  const legacy = data.legacy_settlement_candidates_count || 0;
  const orphans = (data.commission_orphans?.null_client_id || 0)
                + (data.commission_orphans?.missing_client_doc_sample || 0);
  if (legacy === 0 && orphans === 0) return null;
  return (
    <div className="mt-2" data-testid="ssot-integrity-chip-row">
      <button
        type="button"
        onClick={() => navigate('/admin/funded-mismatches')}
        className="flex items-center gap-3 px-4 py-2 rounded-lg border border-rose-300 bg-rose-50 hover:bg-rose-100 transition-colors w-full md:w-auto"
        data-testid="ssot-integrity-chip"
      >
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-rose-600 text-white text-sm font-bold">
          {legacy + orphans}
        </span>
        <span className="text-sm font-medium text-rose-900">SSOT Integrity</span>
        <span className="text-[11px] text-rose-700">
          {legacy} legacy-only deals · {orphans} commission orphans
        </span>
        <span className="text-[10px] text-rose-700 uppercase tracking-wider ml-auto">review →</span>
      </button>
    </div>
  );
};


// v3.15-1036: REMOVED — `AdminDiagnosticsPanel` collapsed-on-dashboard
// surface deleted. All diagnostic widgets are now consumed from
// /app/frontend/src/pages/AdminDiagnosticsPage.jsx which imports
// ReconcileAllChip / SSOTIntegrityChip / BookingSlugIssuesWarning /
// RecentEmailSendsSection directly. The operational dashboard no longer
// shows any audit / reconciliation / migration surface. Per v3.15-1036 spec.


// v3.15-1029: Recent Email Sends — admin-only live communication-integrity
// monitor. Hits /api/admin/communication-audit?limit=10 and shows the last
// 10 outbound emails with their ownership-explanation badges.
// v3.15-1036: Exported for reuse by AdminDiagnosticsPage.
export const RecentEmailSendsSection = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/admin/communication-audit?limit=10');
        if (alive) setItems(res.data?.events || []);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (loading || !items || items.length === 0) return null;
  const fmtAgo = (iso) => {
    if (!iso) return '';
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch { return ''; }
  };
  return (
    <div className="mt-4" data-testid="recent-email-sends-section">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">Recent Email Sends</span>
            <span className="text-[10px] text-slate-500">— Communication integrity monitor</span>
          </div>
          <span className="text-[11px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-testid="recent-email-sends-count">
            {items.length}
          </span>
        </div>
        <div className="space-y-1.5">
          {items.map((e, i) => (
            <button
              key={e.tracking_id || i}
              onClick={() => e.client_id && navigate(`/clients/${e.client_id}`)}
              className="w-full text-left flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
              data-testid={`recent-email-row-${i}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-3 min-w-0">
                <span className="text-[12px] font-medium text-slate-900 truncate max-w-[200px]" title={e.to_email}>{e.to_email || '—'}</span>
                <span className="text-[11px] text-slate-500 truncate max-w-[180px]" title={e.client_name}>{e.client_name || '(no client)'}</span>
                <span className="text-[10px] uppercase tracking-wide text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{e.email_kind || 'workflow'}</span>
                {e.matched_ownership_field ? (
                  <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded" title={e.explanation}>
                    {e.matched_ownership_field}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title={e.explanation}>
                    out-of-scope
                  </span>
                )}
              </div>
              <span className="text-[10px] text-slate-500 whitespace-nowrap">{fmtAgo(e.timestamp)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};


// v3.15-935: Hard to Reach Clients — Dashboard card listing clients flagged
// via POST /api/clients/:id/hard-to-reach. Scoped server-side to what the
// viewer is allowed to see. Click a row to open the deal.
// v3.18-1278 GHOST-OPS-001 — Adds row-level Delete + Final Push actions.
const HardToReachClientsSection = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [activeClient, setActiveClient] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/dashboard/hard-to-reach');
        if (alive) setItems(res.data?.clients || []);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (loading || !items || items.length === 0) return null;

  const fmtAgo = (iso) => {
    if (!iso) return '';
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60)       return 'just now';
    if (s < 3600)     return `${Math.floor(s / 60)}m ago`;
    if (s < 86400)    return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400*7)  return `${Math.floor(s / 86400)}d ago`;
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch { return ''; }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remove ${c.full_name || 'this client'} from the Hard-to-Reach queue? This writes an immutable audit entry.`)) return;
    setBusyId(c.id);
    try {
      await api.delete(`/dashboard/hard-to-reach/${c.id}`);
      toast.success('Removed from Hard-to-Reach queue.');
      setItems((prev) => (prev || []).filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove.');
    } finally {
      setBusyId(null);
    }
  };

  const openFinalPush = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/clients/${c.id}/final-push/draft`, {});
      setActiveClient(c);
      setDraft(data);
      setDraftOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Final Push draft.');
    } finally {
      setBusyId(null);
    }
  };

  const sendFinalPush = async ({ subject, body, to, cc, force }) => {
    if (!activeClient?.id) return { ok: false };
    try {
      const { data } = await api.post(`/clients/${activeClient.id}/final-push/send`, {
        subject, body, to, cc, force: !!force,
      });
      toast.success(`Final Push sent to ${activeClient.full_name || 'client'}.`);
      setItems((prev) => (prev || []).filter((x) => x.id !== activeClient.id));
      setDraftOpen(false);
      setDraft(null);
      setActiveClient(null);
      return { ok: true, data };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === 'object' && detail.error === 'final_push_already_sent') {
        if (window.confirm('A Final Push has already been sent for this Hard-to-Reach event. Send another one anyway?')) {
          return sendFinalPush({ subject, body, to, cc, force: true });
        }
        return { ok: false };
      }
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to send Final Push.');
      return { ok: false };
    }
  };

  return (
    <div className="mt-4" data-testid="hard-to-reach-section">
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">
              Hard to Reach Clients
            </h2>
          </div>
          <span className="text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="hard-to-reach-count">
            {items.length}
          </span>
        </div>
        <div className="divide-y divide-amber-100 rounded-lg bg-white border border-amber-100 overflow-hidden">
          {items.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-amber-50/60 transition-colors"
              data-testid={`hard-to-reach-row-${c.id}`}
            >
              <button
                type="button"
                onClick={() => navigate(`/clients/${c.id}`)}
                className="min-w-0 flex-1 text-left"
                data-testid={`hard-to-reach-open-${c.id}`}
              >
                <div className="text-sm font-semibold text-slate-800 truncate">{c.full_name || '(no name)'}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {c.assigned_rep ? `Rep: ${c.assigned_rep}` : 'Unassigned'}
                  {c.deal_stage ? ` · ${c.deal_stage}` : ''}
                </div>
              </button>
              <div className="text-[11px] text-amber-700 whitespace-nowrap" data-testid={`hard-to-reach-since-${c.id}`}>
                {fmtAgo(c.last_contact_attempt_at || c.hard_to_reach_since)}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === c.id || !c.email}
                  onClick={() => openFinalPush(c)}
                  className="h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100"
                  data-testid={`final-push-btn-${c.id}`}
                  title={c.email ? 'Send one final coordinated outreach email' : 'No email on file for this client'}
                >
                  Final Push
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busyId === c.id}
                  onClick={() => handleDelete(c)}
                  className="h-7 w-7 p-0 text-slate-500 hover:text-rose-700"
                  data-testid={`hard-to-reach-delete-${c.id}`}
                  title="Remove from Hard-to-Reach queue"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {draftOpen && draft && activeClient && (
        <ComposeAndApprove
          open={draftOpen}
          onOpenChange={(v) => { if (!v) { setDraftOpen(false); setDraft(null); setActiveClient(null); } }}
          initial={{
            template_key: 'blank',
            subject:      draft.subject || '',
            body:         draft.body    || '',
            to:           draft.to      || [],
            cc:           draft.cc      || [],
            client_id:    activeClient.id,
            context:      { client_first_name: (activeClient.full_name || '').split(' ')[0] || 'there' },
          }}
          onCustomSend={async (payload) => {
            // v3.18-1290 — Route through the specialized Final Push
            // backend so duplicate-detection (409 final_push_already_sent)
            // and its own audit trail continue to fire.
            const forced = { subject: payload.subject, body: payload.body, to: payload.to, cc: payload.cc, force: !!draft.active_conflict };
            const result = await sendFinalPush(forced);
            if (!result?.ok) {
              // Signal ComposeAndApprove to keep the dialog open so the
              // operator can retry / cancel without losing edits.
              return { __cancel: true };
            }
            return result.data;
          }}
        />
      )}
    </div>
  );
};

// v3.18-1290 EMAIL-ARCH-002 Slice 2 — the bespoke FinalPushComposer
// (previously v3.18-1278) has been retired.  Final Push now uses the
// universal ComposeAndApprove component with an onCustomSend adapter
// that routes to /clients/{id}/final-push/send so the specialized
// duplicate-detection + audit trail continue to fire unchanged.



const DashboardPage = () => {
  useDocumentTitle('Dashboard');
  const { user, isInternal, isAdmin, isPtcUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [activities, setActivities] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [partnerPerformance, setPartnerPerformance] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
    // Load dismissed announcements from localStorage
    try {
      return JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  
  // Application queue widget state (for PTC users only)
  const [applicationQueue, setApplicationQueue] = useState({ count: 0, applications: [] });
  // v3.15-972: missedAppsWidget state removed (Missed Applications block deleted).
  const [applicationsTodayWidget, setApplicationsTodayWidget] = useState({ assigned: 0, claimed: 0, missed: 0 });
  const [repLeaderboard, setRepLeaderboard] = useState([]);
  
  // v3.14-108: Dashboard view toggle - Action view is default
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('dashboardViewMode') || 'action';
  });

  // v3.14-379: Ghost login modal — show once per login session, PTC internal ONLY
  const [ghostModalOpen, setGhostModalOpen] = useState(() => {
    if (sessionStorage.getItem('ghost_briefing_shown')) return false;
    // Only show for internal PTC users
    const companyType = user?.company_type || localStorage.getItem('company_type');
    if (companyType !== 'internal') return false;
    sessionStorage.setItem('ghost_briefing_shown', 'true');
    return true;
  });

  // Chart colors for partners
  const PARTNER_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

  // Announcement priority config
  const announcementConfig = {
    info: { bg: 'bg-blue-50 border-blue-200', icon: Info, iconColor: 'text-blue-500' },
    warning: { bg: 'bg-orange-50 border-orange-200', icon: AlertTriangle, iconColor: 'text-orange-500' },
    critical: { bg: 'bg-red-50 border-red-200', icon: AlertCircle, iconColor: 'text-red-500' },
  };

  const dismissAnnouncement = (id) => {
    const newDismissed = [...dismissedAnnouncements, id];
    setDismissedAnnouncements(newDismissed);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
  };

  const visibleAnnouncements = announcements.filter(a => !dismissedAnnouncements.includes(a.id));

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyWithSign = (value) => {
    if (!value && value !== 0) return '$0';
    const prefix = value >= 0 ? '+' : '';
    return prefix + new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '0%';
    return `${value.toFixed(1)}%`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data with individual error handling
        const [statsRes, pipelineRes, activitiesRes, clientsRes, announcementsRes] = await Promise.allSettled([
          api.get('/dashboard/stats'),
          api.get('/dashboard/pipeline'),
          api.get('/activities/recent?limit=10'),
          api.get('/clients?limit=5'),
          api.get('/announcements/active'),
        ]);

        // Set data from successful responses
        if (statsRes.status === 'fulfilled') {
          setStats(statsRes.value.data);
          setStatsError(null);
        } else {
          console.error('[Dashboard] Stats API failed:', statsRes.reason?.response?.status, statsRes.reason?.message);
          setStatsError(statsRes.reason?.response?.status === 401 ? 'auth' : 'network');
        }
        if (pipelineRes.status === 'fulfilled') setPipeline(pipelineRes.value.data?.pipeline || pipelineRes.value.data || []);
        if (activitiesRes.status === 'fulfilled') setActivities(activitiesRes.value.data);
        // v3.14-114: Handle {clients: [...]} format from API
        if (clientsRes.status === 'fulfilled') {
          const clientsData = clientsRes.value.data?.clients || clientsRes.value.data || [];
          setRecentClients(Array.isArray(clientsData) ? clientsData.slice(0, 5) : []);
        }
        if (announcementsRes.status === 'fulfilled') setAnnouncements(announcementsRes.value.data || []);

        // Fetch partner performance for internal PTC leadership
        if (isInternal() && ['ptc_admin', 'ptc_director', 'ptc_manager'].includes(user?.role)) {
          try {
            const partnerRes = await api.get('/analytics/partner-performance');
            setPartnerPerformance(partnerRes.data);
          } catch (err) {
            console.warn('Failed to fetch partner performance:', err);
          }
        }
        
        // Fetch application queue widget for PTC users only.
        // v3.15-972: missed-applications-widget call removed — block deleted.
        if (isPtcUser()) {
          try {
            const [queueRes, todayRes, leaderboardRes] = await Promise.allSettled([
              api.get('/dashboard/application-queue-widget'),
              api.get('/dashboard/applications-today-widget'),
              api.get('/dashboard/rep-leaderboard'),
            ]);
            
            if (queueRes.status === 'fulfilled') {
              setApplicationQueue(queueRes.value.data);
            }
            if (todayRes.status === 'fulfilled') {
              setApplicationsTodayWidget(todayRes.value.data);
            }
            if (leaderboardRes.status === 'fulfilled') {
              setRepLeaderboard(leaderboardRes.value.data || []);
            }
          } catch (err) {
            console.warn('Failed to fetch application widgets:', err);
          }
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isInternal, isPtcUser, user?.role]);

  // Large Executive Card for Layer 1
  const ExecutiveCard = ({ title, value, subValue, icon: Icon, accent = false, className = '' }) => (
    <Card className={`stat-card ${className}`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">{title}</p>
            <p className={`text-3xl font-bold mt-2 ${accent ? 'text-accent' : 'text-slate-900'}`}>{value}</p>
            {subValue && (
              <p className="text-sm text-slate-500 mt-1">{subValue}</p>
            )}
          </div>
          <div className={`p-4 rounded-xl ${accent ? 'bg-accent/10' : 'bg-slate-100'}`}>
            <Icon className={`h-8 w-8 ${accent ? 'text-accent' : 'text-slate-600'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Pipeline Growth Card (Admin Only)
  const PipelineGrowthCard = ({ value }) => {
    const isPositive = value >= 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? 'text-emerald-600' : 'text-red-600';
    const bgClass = isPositive ? 'bg-emerald-100' : 'bg-red-100';
    
    return (
      <Card className="stat-card" data-testid="stat-pipeline-growth">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Pipeline Growth</p>
              <p className={`text-3xl font-bold mt-2 ${colorClass}`}>
                {formatCurrencyWithSign(value)}
              </p>
              <p className="text-sm text-slate-500 mt-1">vs last week</p>
            </div>
            <div className={`p-4 rounded-xl ${bgClass}`}>
              <Icon className={`h-8 w-8 ${colorClass}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Check if user is PTC Admin (for Pipeline Growth display)
  const showPipelineGrowth = stats?.is_internal_user && stats?.pipeline_growth !== null && stats?.pipeline_growth !== undefined;

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* v3.15-1226 OPS-001 — Priority Queue permanent widget for every user */}
      {/* v3.15-1235 — PriorityQueueWidget removed. See the right-side Ghost Daily Organizer. */}

      {/* v3.15-1130: Appointment Awareness Banner — fires on appointments
          starting within 20 minutes OR appointments where the start time
          has already passed without an outcome being logged. PTC users only. */}
      {isPtcUser() && <AppointmentAlertBanner />}

      {/* Announcement Banner */}
      {visibleAnnouncements.length > 0 && (
        <div className="space-y-2" data-testid="announcement-banners">
          {visibleAnnouncements.map((ann) => {
            const config = announcementConfig[ann.priority] || announcementConfig.info;
            const Icon = config.icon;
            return (
              <div
                key={ann.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${config.bg}`}
                data-testid={`announcement-${ann.id}`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${config.iconColor}`} />
                  <div>
                    <span className="font-medium text-slate-800">{ann.title}</span>
                    <span className="mx-2 text-slate-400">-</span>
                    <span className="text-slate-600">{ann.message}</span>
                  </div>
                </div>
                <button
                  onClick={() => dismissAnnouncement(ann.id)}
                  className="p-1 hover:bg-white/50 rounded"
                  aria-label="Dismiss"
                  data-testid={`dismiss-announcement-${ann.id}`}
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* v3.14-108: Dashboard View Toggle */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'action' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setViewMode('action');
              localStorage.setItem('dashboardViewMode', 'action');
            }}
            data-testid="view-mode-action"
          >
            <AlertCircle className="h-4 w-4 mr-2" />
            Action View
          </Button>
          <Button
            variant={viewMode === 'detailed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setViewMode('detailed');
              localStorage.setItem('dashboardViewMode', 'detailed');
            }}
            data-testid="view-mode-detailed"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Detailed View
          </Button>
        </div>
        <p className="text-sm text-slate-500">
          {viewMode === 'action' 
            ? 'Showing items requiring your attention' 
            : 'Showing detailed analytics & metrics'}
        </p>
      </div>

      {/* v3.15-1072: Validation warning banner REMOVED.
          The forensic validation log is preserved in /api/dashboard/stats.validation_log
          and is now surfaced via /api/admin/reconciliation/v1072 for CEO reporting.
          Money Manager is exposed as a separate "Wire Ledger" tile below — the
          gap between SSOT-funded and ledger-tracked wires is VISIBLE, not hidden. */}

      {/* v3.14-202: Stats load failure banner */}
      {statsError && !stats && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200" data-testid="stats-error-banner">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">
              {statsError === 'auth' 
                ? 'Session expired — please log out and back in to refresh your data.' 
                : 'Failed to load dashboard data. Check your connection.'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="text-red-700 border-red-300 hover:bg-red-100">
            Refresh
          </Button>
        </div>
      )}

      {/* v3.14-160: COMPACT FINANCIAL METRICS — TOP ROW (Always visible, both views)
           v3.15-1072: grid widened to 6 cols on lg+ to fit Wire Ledger tile. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="metrics-top-row">
        <Card className="border-l-4 border-l-emerald-500" data-testid="stat-total-funded-volume">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Total Funded</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{formatCurrency(stats?.total_funded_volume)}</p>
            <p className="text-[11px] text-slate-400">{stats?.total_funded_deals || 0} deals</p>
            <p className="text-[9px] text-slate-400 italic mt-0.5">SSOT: All Parties Settled</p>
          </CardContent>
        </Card>
        {/* v3.15-1072 / v3.15-1073: Wire Ledger tile — PTC Admin role only.
            Money Manager data is executive ledger; hidden for everyone else. */}
        {isAdmin() && (
          <Card
            className="border-l-4 border-l-teal-500 cursor-pointer hover:shadow-md transition-shadow"
            data-testid="stat-wire-ledger"
            onClick={() => navigate('/money-manager')}
            title="Money Manager — ledger-tracked wires. NOT the funded SSOT."
          >
            <CardContent className="p-3">
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Wire Ledger</p>
              <p className="text-xl font-bold text-teal-700 mt-1">{formatCurrency(stats?.wire_ledger?.total_amount || 0)}</p>
              <p className="text-[11px] text-slate-400">{stats?.wire_ledger?.row_count || 0} ledger rows</p>
              <p className="text-[9px] text-slate-400 italic mt-0.5">
                Money Manager (ledger only)
              </p>
            </CardContent>
          </Card>
        )}
        {/* v3.15-913: clickable for admins → drills into the itemized monthly report. */}
        <Card
          className={`border-l-4 border-l-blue-500 ${isInternal() ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
          data-testid="stat-funded-this-month"
          onClick={() => { if (isInternal()) navigate('/admin/funded-this-month'); }}
        >
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Funded This Month {isInternal() && <span className="text-[9px] text-blue-500 ml-1">(view items →)</span>}</p>
            <p className="text-xl font-bold text-blue-600 mt-1">{formatCurrency(stats?.funded_this_month)}</p>
            <p className="text-[11px] text-slate-400">{stats?.funded_this_month_count || 0} deals</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500" data-testid="stat-pending-funding">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Pending Funding</p>
            <p className="text-xl font-bold text-amber-600 mt-1">{stats?.pending_funding_count || 0}</p>
            <p className="text-[11px] text-slate-400">{formatCurrency(stats?.pending_funding_value)} value</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500" data-testid="stat-pipeline-value">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Pipeline Value</p>
            <p className="text-xl font-bold text-purple-600 mt-1">{formatCurrency(stats?.pipeline_value)}</p>
            <p className="text-[11px] text-slate-400">{stats?.total_clients || 0} total clients</p>
          </CardContent>
        </Card>
        {isInternal() && (
          <Card className="border-l-4 border-l-cyan-500" data-testid="stat-settlement">
            <CardContent className="p-3">
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Settlement</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm font-semibold text-emerald-600">{stats?.settled_count || 0} Paid</span>
                <span className="text-slate-300">|</span>
                <span className="text-sm font-semibold text-amber-600">{stats?.due_count || 0} Due</span>
              </div>
              <p className="text-[11px] text-slate-400">{formatCurrency(stats?.total_ptc_fees)} PTC fees</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* v3.15-1036: Dashboard cleanup — ALL diagnostic / integrity /
          reconciliation / migration tooling has been REMOVED from the
          operational dashboard. The deal lifecycle SSOT
          (`all_parties_settled == true`) is the only operational truth for
          funding/payroll/commissions. Diagnostics now live exclusively under
          Admin → Diagnostics (/admin/diagnostics):
            - AdminDiagnosticsPanel (Reconcile / SSOT Integrity / Booking Slug)
            - Recent Email Sends (communication integrity)
            - Unverified Funded Deals table
          See FUNDING_SSOT_AUDIT_v3_15_1036.md for the migration manifest. */}

      {/* v3.15-935: Hard to Reach Clients — operational signal, stays on
          the main dashboard. Unresponsive clients flagged via the toggle
          on Client Detail. Each row is clickable. */}
      <HardToReachClientsSection />

      {/* v3.15-1036: Unverified Funded Deals table moved to Admin → Diagnostics.
          This was a migration/audit surface, not an operational signal. The
          official funded totals never included these rows; surfacing them on
          the main dashboard caused confusion. Admins can review and promote
          via /admin/diagnostics. */}

      {/* v3.14-160: Action-Focused Dashboard (Secondary — below metrics) */}
      {viewMode === 'action' && (
        <ActionFocusedDashboard />
      )}

      {/* Legacy Detailed View - Only shown when toggled */}
      {viewMode === 'detailed' && (
        <>
      {/* PTC WORKFLOW DASHBOARD - 3 Column Layout (PTC Users Only) */}
      {isPtcUser() && (
        <WorkflowDashboard />
      )}

      {/* LAYER 2: Pipeline Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Overview */}
        <Card className="lg:col-span-2" data-testid="pipeline-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Deal Pipeline</CardTitle>
            <Link to="/clients">
              <Button variant="ghost" size="sm" className="text-accent hover:text-accent/80">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pipeline.filter(item => item.count > 0 || ['new_lead', 'contacted', 'application_submitted', 'under_review', 'approved', 'funding_in_progress', 'funded'].includes(item.stage)).map((item) => (
                <div key={item.stage} className="flex items-center gap-4">
                  <div className="w-40 text-sm text-slate-600 capitalize">
                    {item.stage.replace(/_/g, ' ')}
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent rounded-full transition-all duration-500"
                        style={{
                          width: `${stats?.total_clients ? (item.count / stats.total_clients) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="w-12 text-right font-medium text-slate-700">{item.count}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* v3.18-1306 — Recent Activity widget removed per V1 cleanup:
            UI-event stream had no operational purpose.  Backend audit
            logs remain intact and are surfaced only in admin diagnostics. */}
      </div>

      {/* Partner Performance Dashboard - PTC Internal Only */}
      {isInternal() && partnerPerformance && ['ptc_admin', 'ptc_director', 'ptc_manager'].includes(user?.role) && (
        <div className="space-y-6" data-testid="partner-performance-section">
          <div>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Partner Performance Analytics
              <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800">PTC Only</Badge>
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top Partners by Funded Volume Chart */}
              <Card className="lg:col-span-2" data-testid="partner-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-accent" />
                    Top Partners by Funded Volume
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    {partnerPerformance.top_partners_by_funded_volume?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={partnerPerformance.top_partners_by_funded_volume.slice(0, 5)}
                          layout="vertical"
                          margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                          <XAxis 
                            type="number" 
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          />
                          <YAxis 
                            type="category" 
                            dataKey="company_name" 
                            width={90}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            formatter={(value) => [formatCurrency(value), 'Funded Volume']}
                            contentStyle={{ borderRadius: 8 }}
                          />
                          <Bar dataKey="total_funded_volume" radius={[0, 4, 4, 0]}>
                            {partnerPerformance.top_partners_by_funded_volume.slice(0, 5).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PARTNER_COLORS[index % PARTNER_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        No partner data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <Card data-testid="partner-summary">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg">Network Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Total Partner Companies</p>
                    <p className="text-2xl font-bold text-slate-800">{partnerPerformance.totals?.total_companies || 0}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-slate-500">Total Deals (Network)</p>
                    <p className="text-2xl font-bold text-blue-700">{partnerPerformance.totals?.total_deals_all || 0}</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <p className="text-sm text-slate-500">Total Funded (Network)</p>
                    <p className="text-2xl font-bold text-emerald-700">{partnerPerformance.totals?.total_funded_all || 0}</p>
                  </div>
                  <div className="p-4 bg-accent/10 rounded-lg">
                    <p className="text-sm text-slate-500">Network Funded Volume</p>
                    <p className="text-2xl font-bold text-accent">{formatCurrency(partnerPerformance.totals?.total_funded_volume_all || 0)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Partner Performance Table */}
          <Card data-testid="partner-table">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">Partner Company Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Company Name
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Deals Submitted
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Deals Funded
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Deal Volume
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Funded Volume
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Approval Rate
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Funding Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerPerformance.partners?.map((partner, index) => (
                      <tr key={partner.company_id} className="border-b border-slate-50 table-row-hover">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: PARTNER_COLORS[index % PARTNER_COLORS.length] }}
                            />
                            <span className="font-medium text-slate-800">{partner.company_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-slate-600">{partner.total_deals}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{partner.funded_deals}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{formatCurrency(partner.total_deal_volume)}</td>
                        <td className="py-3 px-4 text-right font-medium text-emerald-600">{formatCurrency(partner.total_funded_volume)}</td>
                        <td className="py-3 px-4 text-right">
                          <Badge 
                            variant="secondary" 
                            className={partner.approval_rate >= 70 ? 'bg-green-100 text-green-800' : partner.approval_rate >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}
                          >
                            {partner.approval_rate}%
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Badge 
                            variant="secondary" 
                            className={partner.funding_rate >= 70 ? 'bg-green-100 text-green-800' : partner.funding_rate >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}
                          >
                            {partner.funding_rate}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {(!partnerPerformance.partners || partnerPerformance.partners.length === 0) && (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-slate-500">
                          No partner data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* v3.15-925: Upcoming Appointments widget — replaces Google Calendar.
          Today/Tomorrow/Future grouping scoped to rep or full-company for admins. */}
      {isPtcUser() && (
        <div className="grid grid-cols-1 gap-6" data-testid="upcoming-appointments-row">
          <UpcomingAppointmentsWidget />
        </div>
      )}

      {/* v3.18-1306 — Application Queue widget removed per V1 cleanup.
          The dedicated `/ptc-deals` page remains the authoritative queue;
          this dashboard tile was showing seeded/test data and duplicating
          information already surfaced elsewhere. */}

      {/* Applications Today & Rep Leaderboard - Internal Users Only */}
      {isPtcUser() && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="performance-widgets">
          {/* Applications Today Widget */}
          <Card data-testid="applications-today-widget">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Gauge className="h-5 w-5 text-indigo-600" />
                Applications Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{applicationsTodayWidget.assigned}</p>
                  <p className="text-xs text-slate-500 mt-1">Assigned</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{applicationsTodayWidget.claimed}</p>
                  <p className="text-xs text-slate-500 mt-1">Claimed</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{applicationsTodayWidget.missed}</p>
                  <p className="text-xs text-slate-500 mt-1">Missed</p>
                </div>
              </div>
              {applicationsTodayWidget.assigned > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>Claim Rate</span>
                    <span className="font-medium">
                      {Math.round((applicationsTodayWidget.claimed / applicationsTodayWidget.assigned) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (applicationsTodayWidget.claimed / applicationsTodayWidget.assigned) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rep Leaderboard Widget */}
          <Card className="lg:col-span-2" data-testid="rep-leaderboard-widget">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Rep Performance Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              {repLeaderboard.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-2 px-2 text-slate-500 font-medium">#</th>
                        <th className="text-left py-2 px-2 text-slate-500 font-medium">Rep Name</th>
                        <th className="text-center py-2 px-2 text-slate-500 font-medium">Assigned</th>
                        <th className="text-center py-2 px-2 text-slate-500 font-medium">Claim Rate</th>
                        <th className="text-center py-2 px-2 text-slate-500 font-medium">Avg Response</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repLeaderboard.slice(0, 5).map((rep, idx) => (
                        <tr key={rep.rep_id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2 px-2">
                            {idx === 0 ? (
                              <span className="text-yellow-500 font-bold">🥇</span>
                            ) : idx === 1 ? (
                              <span className="text-slate-400 font-bold">🥈</span>
                            ) : idx === 2 ? (
                              <span className="text-amber-600 font-bold">🥉</span>
                            ) : (
                              <span className="text-slate-400">{idx + 1}</span>
                            )}
                          </td>
                          <td className="py-2 px-2 font-medium text-slate-700">{rep.rep_name}</td>
                          <td className="py-2 px-2 text-center text-slate-600">{rep.applications_assigned}</td>
                          <td className="py-2 px-2 text-center">
                            <Badge 
                              variant="secondary" 
                              className={
                                rep.claim_rate >= 90 ? 'bg-green-100 text-green-700' :
                                rep.claim_rate >= 70 ? 'bg-blue-100 text-blue-700' :
                                rep.claim_rate >= 50 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }
                            >
                              {rep.claim_rate}%
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-center text-slate-600">
                            {rep.avg_response_time_seconds ? (
                              <span className={rep.avg_response_time_seconds < 60 ? 'text-green-600' : 'text-slate-600'}>
                                {rep.avg_response_time_seconds < 60 
                                  ? `${Math.round(rep.avg_response_time_seconds)}s`
                                  : `${Math.round(rep.avg_response_time_seconds / 60)}m ${Math.round(rep.avg_response_time_seconds % 60)}s`
                                }
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No performance data yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Clients */}
      <Card data-testid="recent-clients-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Recent Clients</CardTitle>
          <Link to="/clients">
            <Button variant="ghost" size="sm" className="text-accent hover:text-accent/80">
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentClients.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No clients yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Client
                    </th>
                    {isPtcUser() && (
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Company
                      </th>
                    )}
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Deal Stage
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Deal Amount
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Director
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Sales Rep
                    </th>
                    {isPtcUser() && (
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        PTC Rep
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {recentClients.map((client) => (
                    <tr
                      key={client.id}
                      className="border-b border-slate-50 table-row-hover cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => navigate(`/clients/${client.id}`)}
                      data-testid={`deal-row-${client.id}`}
                    >
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-blue-600 hover:underline">{client.full_name}</p>
                          <p className="text-sm text-slate-500">{client.email}</p>
                        </div>
                      </td>
                      {isPtcUser() && (
                        <td className="py-3 px-4 text-sm text-slate-600">{client.company_name}</td>
                      )}
                      <td className="py-3 px-4">
                        <Badge className={stageColors[client.deal_stage]} variant="secondary">
                          {(client.deal_stage || 'new_lead').replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {client.deal_amount ? `$${client.deal_amount.toLocaleString()}` : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {client.director_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {client.sales_rep_name || '-'}
                      </td>
                      {isPtcUser() && (
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {client.ptc_rep_name || '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
